import { useState, useEffect, useCallback } from 'react';
import {
  Home,
  Plus,
  Edit,
  Eye,
  Archive,
  RotateCcw,
  Search,
  Loader2,
  Upload,
  X,
  Share,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  ScanSearch,
  Trash2,
  Link2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Copy,
  ExternalLink,
  UserCheck,
  Users,
  Unlink,
  RefreshCw,
  ArrowRightLeft,
  Zap,
  Tag,
  Star,
  Building2,
  UserPlus,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Separator } from './ui/separator';
import { Property } from '../types/property';
import { Agent } from '../types/agent';
import { Seller } from '../types/seller';
import { PropertyDetailModal } from './PropertyDetailModal';
import { propertyAPI } from '../lib/property-api';
import { externalListingsAPI } from '../lib/external-api';
import { getSomethingPropertyId } from '../lib/listing-adapter';
import { toast } from 'sonner';
import { logger } from '../services/logger.service';

interface AdminPropertyManagementProps {
  properties: Property[];
  isLoading: boolean;
  statusFilter: string;
  searchTerm: string;
  selectedProperties: string[];
  formData: any;
  uploadedImages: Array<{ url: string; path: string }>;
  isUploadingImage: boolean;
  uploadProgress?: { current: number; total: number } | null;
  isSubmitting: boolean;
  editingProperty: Property | null;
  isCreatingProperty: boolean;
  onStatusFilterChange: (filter: string) => void;
  onSearchChange: (term: string) => void;
  onCreateProperty: () => void;
  onEditProperty: (property: Property) => void;
  onArchiveProperty: (property: Property) => void;
  onRestoreProperty: (property: Property) => void;
  onToggleSelection: (propertyId: string) => void;
  onBulkArchive: () => void;
  onFormChange: (data: any) => void;
  onImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: (index: number) => void;
  onMoveImage?: (index: number, direction: 'left' | 'right') => void;
  onSubmit: (e: React.FormEvent) => void;
  onCloseDialog: () => void;
  onPropertiesChanged?: () => void;
}

export function AdminPropertyManagement({
  properties,
  isLoading,
  statusFilter,
  searchTerm,
  selectedProperties,
  formData,
  uploadedImages,
  isUploadingImage,
  uploadProgress,
  isSubmitting,
  editingProperty,
  isCreatingProperty,
  onStatusFilterChange,
  onSearchChange,
  onCreateProperty,
  onEditProperty,
  onArchiveProperty,
  onRestoreProperty,
  onToggleSelection,
  onBulkArchive,
  onFormChange,
  onImageUpload,
  onRemoveImage,
  onMoveImage,
  onSubmit,
  onCloseDialog,
  onPropertiesChanged,
}: AdminPropertyManagementProps) {
  // Property detail modal state
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // ─── Deduplication state ───
  const [dedupExpanded, setDedupExpanded] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [scanResults, setScanResults] = useState<{
    totalProperties: number;
    duplicateGroups: number;
    byExternalId: Array<{ externalId: string; count: number; ids: string[]; titles: string[] }>;
    byFingerprint: Array<{ fingerprint: string; count: number; ids: string[]; titles: string[] }>;
  } | null>(null);
  const [cleanResults, setCleanResults] = useState<{
    success: boolean;
    duplicatesRemoved: number;
    groupsMerged: number;
    removedKeys: string[];
  } | null>(null);

  // ─── Link property dialog state ───
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkingProperty, setLinkingProperty] = useState<Property | null>(null);
  const [linkForm, setLinkForm] = useState({ agentId: '', sellerId: '', externalAgentId: '', externalId: '' });
  const [isLinking, setIsLinking] = useState(false);

  // ─── Dropdown data for link dialog ───
  const [agentsList, setAgentsList] = useState<Agent[]>([]);
  const [sellersList, setSellersList] = useState<Seller[]>([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');

  // ─── Agent External Mapping state ───
  const [agentMapExpanded, setAgentMapExpanded] = useState(false);
  const [agentMapList, setAgentMapList] = useState<Agent[]>([]);
  const [isLoadingAgentMap, setIsLoadingAgentMap] = useState(false);
  const [agentMapSearch, setAgentMapSearch] = useState('');
  const [editingAgentLink, setEditingAgentLink] = useState<string | null>(null);
  const [editingExternalId, setEditingExternalId] = useState('');
  const [isSavingAgentLink, setIsSavingAgentLink] = useState(false);

  // ─── Listing Status Management state ───
  const [statusPanelExpanded, setStatusPanelExpanded] = useState(false);
  const [statusUpdateProperty, setStatusUpdateProperty] = useState<Property | null>(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusForm, setStatusForm] = useState({
    status: '' as string,
    listingNumber: '',
    listingUrl: '',
    message: '',
  });
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusSearchTerm, setStatusSearchTerm] = useState('');

  // ─── SP ID Backfill state ───
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [backfillResults, setBackfillResults] = useState<{
    backfilled: number;
    skipped: number;
    errors: number;
    total: number;
    details: Array<{ id: string; somethingPropertyId: string }>;
  } | null>(null);

  // ─── Purge state ───
  const [isPurging, setIsPurging] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  // ─── Featured toggle state ───
  const [featuredTogglingId, setFeaturedTogglingId] = useState<string | null>(null);

  // ─── Source filter (internal) ───
  const [sourceFilter, setSourceFilter] = useState<'all' | 'property24' | 'seller' | 'manual'>('all');

  // Load agents & sellers for dropdowns when link dialog opens
  const loadDropdownData = useCallback(async () => {
    setIsLoadingDropdowns(true);
    try {
      const [agents, sellers] = await Promise.all([
        propertyAPI.getAgents(),
        propertyAPI.getSellers(),
      ]);
      setAgentsList(agents);
      setSellersList(sellers);
    } catch (error) {
      logger.error('Error loading dropdown data', error as Error, undefined, 'AdminPropertyManagement');
    } finally {
      setIsLoadingDropdowns(false);
    }
  }, []);

  // Load agents for the external mapping panel
  const loadAgentMapData = useCallback(async () => {
    setIsLoadingAgentMap(true);
    try {
      const agents = await propertyAPI.getAgents();
      setAgentMapList(agents);
    } catch (error) {
      logger.error('Error loading agent map data', error as Error, undefined, 'AdminPropertyManagement');
    } finally {
      setIsLoadingAgentMap(false);
    }
  }, []);

  // Filtered agents for agent dropdown search
  const filteredAgentsDropdown = agentsList.filter(a =>
    !agentSearchTerm ||
    a.name?.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
    a.email?.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
    a.id?.toLowerCase().includes(agentSearchTerm.toLowerCase())
  );

  // Filtered agents for agent mapping panel
  const filteredAgentMap = agentMapList.filter(a =>
    !agentMapSearch ||
    a.name?.toLowerCase().includes(agentMapSearch.toLowerCase()) ||
    a.email?.toLowerCase().includes(agentMapSearch.toLowerCase()) ||
    a.externalAgentId?.toLowerCase().includes(agentMapSearch.toLowerCase())
  );

  // ─── Dedup handlers ───
  const handleScanDuplicates = async () => {
    setIsScanning(true);
    setCleanResults(null);
    try {
      const results = await propertyAPI.scanDuplicates();
      setScanResults(results);
      if (results.duplicateGroups === 0) {
        toast.success('No duplicates found — your data is clean!');
      } else {
        toast.warning(`Found ${results.duplicateGroups} duplicate group(s) across ${results.totalProperties} properties`);
      }
    } catch (error: any) {
      logger.error('Error scanning duplicates', error as Error, undefined, 'AdminPropertyManagement');
      toast.error(error?.message || 'Failed to scan for duplicates');
    } finally {
      setIsScanning(false);
    }
  };

  const handleCleanDuplicates = async () => {
    if (!confirm('This will merge duplicate properties and remove extra copies. The newest/best record is kept. Continue?')) return;
    setIsCleaning(true);
    try {
      const results = await propertyAPI.cleanDuplicates();
      setCleanResults(results);
      setScanResults(null);
      toast.success(`Cleaned ${results.duplicatesRemoved} duplicate(s) across ${results.groupsMerged} group(s)`);
      onPropertiesChanged?.();
    } catch (error: any) {
      logger.error('Error cleaning duplicates', error as Error, undefined, 'AdminPropertyManagement');
      toast.error(error?.message || 'Failed to clean duplicates');
    } finally {
      setIsCleaning(false);
    }
  };

  // ─── Link property handlers ───
  const openLinkDialog = (property: Property) => {
    setLinkingProperty(property);
    setLinkForm({
      agentId: property.agentId || '',
      sellerId: property.sellerId || '',
      externalAgentId: property.externalAgentId || '',
      externalId: property.externalId || '',
    });
    setShowLinkDialog(true);
    loadDropdownData();
  };

  const handleLinkProperty = async () => {
    if (!linkingProperty) return;
    if (!linkForm.agentId) {
      toast.error('Please select an agent before saving');
      return;
    }
    setIsLinking(true);
    try {
      // Find selected agent details from the dropdown
      const selectedAgent = agentsList.find(a => a.id === linkForm.agentId);
      await propertyAPI.assignAgent(linkingProperty.id, {
        agentId: linkForm.agentId,
        agentName: selectedAgent?.name || undefined,
        agentEmail: selectedAgent?.email || undefined,
        agentPhone: selectedAgent?.phone || undefined,
        agentPhotoUrl: (selectedAgent as any)?.avatar || (selectedAgent as any)?.photoUrl || undefined,
      });
      toast.success(`Agent assigned to "${linkingProperty.title}"`);
      setShowLinkDialog(false);
      setLinkingProperty(null);
      onPropertiesChanged?.();
    } catch (error: any) {
      logger.error('Error assigning agent', error as Error, undefined, 'AdminPropertyManagement');
      toast.error(error?.message || 'Failed to assign agent');
    } finally {
      setIsLinking(false);
    }
  };

  // ─── Featured toggle handler ───
  const handleFeatureToggle = async (property: Property) => {
    // F-018: DynamoDB PK for P24 listings is LISTING#somethingPropertyId (full, not stripped)
    // For manually created listings, PK is LISTING#listingId (= property.id)
    const sId = (property as any).somethingPropertyId as string | undefined;
    const listingId = (sId?.startsWith('SP-PROPERTY24-') ? sId : null) ?? property.id ?? sId;
    const newFeatured = !(property as any).featured;
    setFeaturedTogglingId(listingId);
    try {
      await propertyAPI.featureListing(listingId, newFeatured);
      toast.success(newFeatured ? `"${property.title}" marked as featured` : `"${property.title}" removed from featured`);
      onPropertiesChanged?.();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update featured status');
    } finally {
      setFeaturedTogglingId(null);
    }
  };

  const handlePropertyClick = (property: Property) => {
    setSelectedProperty(property);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedProperty(null);
  };

  const handlePropertyUpdated = () => {
    // Refresh the properties list
    onPropertiesChanged?.();
  };

  // ─── Listing Status handlers ───
  const LISTING_STATUSES = ['Active', 'Pending', 'Sold', 'Rented', 'Expired', 'Withdrawn', 'Cancelled'] as const;

  // Status color mapping per design spec v2.1
  // Active=#10B981, Sold=#EF4444, Pending=#F59E0B, Withdrawn=#6B7280
  const statusColorMap: Record<string, string> = {
    Active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    Pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    Sold: 'bg-red-500/20 text-red-400 border-red-500/30',
    Rented: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    Expired: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    Withdrawn: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    Cancelled: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  };

  const openStatusDialog = (property: Property) => {
    setStatusUpdateProperty(property);
    setStatusForm({
      status: property.listingStatus || 'Active',
      listingNumber: '',
      listingUrl: '',
      message: '',
    });
    setShowStatusDialog(true);
  };

  const handleUpdateListingStatus = async () => {
    if (!statusUpdateProperty || !statusForm.status) return;
    setIsUpdatingStatus(true);
    try {
      const spId = getSomethingPropertyId(statusUpdateProperty);
      // Call external API to update status
      await externalListingsAPI.updateListingStatus({
        somethingPropertyId: spId || undefined,
        listingId: statusUpdateProperty.externalId || undefined,
        propertyId: statusUpdateProperty.id,
        status: statusForm.status as any,
        listingNumber: statusForm.listingNumber || undefined,
        listingUrl: statusForm.listingUrl || undefined,
        message: statusForm.message || undefined,
      });

      // Update local KV store
      await propertyAPI.updateLocalListingStatus(statusUpdateProperty.id, statusForm.status);

      toast.success(`Listing status updated to "${statusForm.status}" for "${statusUpdateProperty.title}"`);
      setShowStatusDialog(false);
      setStatusUpdateProperty(null);
      onPropertiesChanged?.();
    } catch (error: any) {
      logger.error('Error updating listing status', error as Error, undefined, 'AdminPropertyManagement');
      // If external API fails, still try to update locally
      try {
        await propertyAPI.updateLocalListingStatus(statusUpdateProperty.id, statusForm.status);
        toast.warning(`External API failed but local status updated to "${statusForm.status}". Error: ${error?.message || 'Unknown'}`);
        setShowStatusDialog(false);
        setStatusUpdateProperty(null);
        onPropertiesChanged?.();
      } catch (localErr: any) {
        toast.error(localErr?.message || error?.message || 'Failed to update listing status');
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ─── SP ID Backfill handler ───
  const handleBackfillSpIds = async () => {
    setIsBackfilling(true);
    setBackfillResults(null);
    try {
      const results = await propertyAPI.backfillSpIds();
      setBackfillResults(results);
      if (results.backfilled === 0) {
        toast.success('All properties already have SP IDs — nothing to backfill!');
      } else {
        toast.success(`Backfilled SP IDs for ${results.backfilled} properties`);
        onPropertiesChanged?.();
      }
    } catch (error: any) {
      logger.error('Error backfilling SP IDs', error as Error, undefined, 'AdminPropertyManagement');
      toast.error(error?.message || 'Failed to backfill SP IDs');
    } finally {
      setIsBackfilling(false);
    }
  };

  // ─── Purge handler ───
  const handlePurgeAll = async () => {
    setIsPurging(true);
    try {
      const result = await propertyAPI.purgeAllProperties();
      toast.success(`Purged ${result.totalDeleted} entries (${result.counts['property:'] || 0} properties, ${result.counts['property_view:'] || 0} views, ${result.counts['seller_property:'] || 0} seller properties)`);
      setShowPurgeConfirm(false);
      onPropertiesChanged?.();
    } catch (error: any) {
      logger.error('Error purging properties', error as Error, undefined, 'AdminPropertyManagement');
      toast.error(error?.message || 'Failed to purge properties');
    } finally {
      setIsPurging(false);
    }
  };

  // Properties with external links (for status panel)
  const externalProperties = properties.filter(p =>
    p.somethingPropertyId || p.externalId || p._external?.listingId
  );

  const filteredStatusProperties = externalProperties.filter(p =>
    !statusSearchTerm ||
    p.title?.toLowerCase().includes(statusSearchTerm.toLowerCase()) ||
    p.somethingPropertyId?.toLowerCase().includes(statusSearchTerm.toLowerCase()) ||
    p.externalId?.toLowerCase().includes(statusSearchTerm.toLowerCase())
  );

  // Count properties missing SP IDs
  const propertiesMissingSpId = properties.filter(p =>
    !p.somethingPropertyId && (p.externalId || p._external?.listingId)
  ).length;

  const stats = {
    total: properties.length,
    published: properties.filter(p => p.status === 'Published').length,
    draft: properties.filter(p => p.status === 'Draft').length,
    inReview: properties.filter(p => p.status === 'In-Review').length,
    archived: properties.filter(p => p.status === 'Archived').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3>Listing Management</h3>
          <p className="text-muted-foreground">
            Manage all listings — Property24, seller-registered, and manual. Assign agents and feature listings.
          </p>
        </div>
        <Button data-testid="admin-property-create-button" onClick={onCreateProperty}>
          <Plus className="w-4 h-4 mr-2" />
          Create Listing
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <Home className="w-8 h-8 text-muted-foreground" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Published</p>
              <p className="text-2xl font-bold text-green-500">{stats.published}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Draft</p>
              <p className="text-2xl font-bold text-yellow-500">{stats.draft}</p>
            </div>
            <Edit className="w-8 h-8 text-yellow-500" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">In Review</p>
              <p className="text-2xl font-bold text-blue-500">{stats.inReview}</p>
            </div>
            <Eye className="w-8 h-8 text-blue-500" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Archived</p>
              <p className="text-2xl font-bold text-gray-500">{stats.archived}</p>
            </div>
            <Archive className="w-8 h-8 text-gray-500" />
          </div>
        </Card>
      </div>

      {/* ═══ Data Quality & Deduplication ═══ */}
      <Card className="border-amber-500/30 bg-amber-500/5">
        <CardHeader
          className="cursor-pointer select-none py-3 px-4"
          onClick={() => setDedupExpanded(!dedupExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              <CardTitle className="text-base">Data Quality & Deduplication</CardTitle>
              {scanResults && scanResults.duplicateGroups > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {scanResults.duplicateGroups} duplicate group{scanResults.duplicateGroups !== 1 ? 's' : ''}
                </Badge>
              )}
              {cleanResults && cleanResults.duplicatesRemoved > 0 && (
                <Badge className="ml-2 bg-green-500/20 text-green-400 border border-green-500/30">
                  {cleanResults.duplicatesRemoved} cleaned
                </Badge>
              )}
            </div>
            {dedupExpanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        {dedupExpanded && (
          <CardContent className="space-y-4 pt-0">
            <p className="text-sm text-muted-foreground">
              Scan for duplicate properties caused by overlapping import paths (e.g. <code className="text-xs bg-muted px-1 py-0.5 rounded">property:ext-123</code> vs <code className="text-xs bg-muted px-1 py-0.5 rounded">property:123</code>).
              Cleaning merges duplicates and keeps the canonical record.
            </p>

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <Button
                data-testid="admin-property-scan-duplicates-button"
                variant="outline"
                size="sm"
                onClick={handleScanDuplicates}
                disabled={isScanning || isCleaning}
              >
                {isScanning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ScanSearch className="w-4 h-4 mr-2" />
                )}
                {isScanning ? 'Scanning...' : 'Scan for Duplicates'}
              </Button>

              {scanResults && scanResults.duplicateGroups > 0 && (
                <Button
                  data-testid="admin-property-clean-duplicates-button"
                  variant="destructive"
                  size="sm"
                  onClick={handleCleanDuplicates}
                  disabled={isCleaning || isScanning}
                >
                  {isCleaning ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  {isCleaning ? 'Cleaning...' : `Clean ${scanResults.duplicateGroups} Group(s)`}
                </Button>
              )}
            </div>

            {/* Scan results */}
            {scanResults && (
              <div className="space-y-3">
                <Separator />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold">{scanResults.totalProperties}</p>
                    <p className="text-xs text-muted-foreground">Total Properties</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-500">{scanResults.duplicateGroups}</p>
                    <p className="text-xs text-muted-foreground">Duplicate Groups</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-500">{scanResults.byExternalId.length}</p>
                    <p className="text-xs text-muted-foreground">By External ID</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-500">{scanResults.byFingerprint.length}</p>
                    <p className="text-xs text-muted-foreground">By Fingerprint</p>
                  </div>
                </div>

                {/* Duplicate groups by externalId */}
                {scanResults.byExternalId.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" />
                      Duplicates by External ID
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {scanResults.byExternalId.map((group) => (
                        <div key={group.externalId} className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant="outline" className="text-xs font-mono">
                              externalId: {group.externalId}
                            </Badge>
                            <Badge variant="destructive" className="text-xs">
                              {group.count} copies
                            </Badge>
                          </div>
                          <div className="space-y-1 mt-2">
                            {group.ids.map((id, idx) => (
                              <div key={id} className="flex items-center gap-2 text-xs">
                                <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-muted-foreground">{id}</code>
                                <span className="text-muted-foreground truncate">{group.titles[idx]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Duplicate groups by fingerprint */}
                {scanResults.byFingerprint.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold flex items-center gap-1.5">
                      <Copy className="w-3.5 h-3.5" />
                      Duplicates by Title+Price Fingerprint
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {scanResults.byFingerprint.map((group) => (
                        <div key={group.fingerprint} className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <Badge variant="outline" className="text-xs font-mono truncate max-w-[300px]">
                              {group.fingerprint}
                            </Badge>
                            <Badge className="text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30">
                              {group.count} copies
                            </Badge>
                          </div>
                          <div className="space-y-1 mt-2">
                            {group.ids.map((id, idx) => (
                              <div key={id} className="flex items-center gap-2 text-xs">
                                <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-muted-foreground">{id}</code>
                                <span className="text-muted-foreground truncate">{group.titles[idx]}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {scanResults.duplicateGroups === 0 && (
                  <Alert className="border-green-500/30 bg-green-500/5">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <AlertTitle className="text-green-400">All Clear</AlertTitle>
                    <AlertDescription className="text-green-400/80">
                      No duplicate properties detected. Your data is consistent.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Clean results */}
            {cleanResults && (
              <div className="space-y-3">
                <Separator />
                <Alert className="border-green-500/30 bg-green-500/5">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <AlertTitle className="text-green-400">Cleanup Complete</AlertTitle>
                  <AlertDescription className="text-green-400/80">
                    Removed {cleanResults.duplicatesRemoved} duplicate record{cleanResults.duplicatesRemoved !== 1 ? 's' : ''} across {cleanResults.groupsMerged} group{cleanResults.groupsMerged !== 1 ? 's' : ''}.
                  </AlertDescription>
                </Alert>
                {cleanResults.removedKeys.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground">Removed KV keys:</p>
                    <div className="bg-muted/50 rounded-lg p-2 max-h-32 overflow-y-auto space-y-0.5">
                      {cleanResults.removedKeys.map((key) => (
                        <code key={key} className="block text-xs font-mono text-muted-foreground">{key}</code>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ═══ Agent External Mapping ═══ */}
      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardHeader
          className="cursor-pointer select-none py-3 px-4"
          onClick={() => {
            const next = !agentMapExpanded;
            setAgentMapExpanded(next);
            if (next && agentMapList.length === 0) loadAgentMapData();
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              <CardTitle className="text-base">Agent External Mapping</CardTitle>
              {agentMapList.length > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {agentMapList.filter(a => a.externalAgentId).length}/{agentMapList.length} linked
                </Badge>
              )}
            </div>
            {agentMapExpanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        {agentMapExpanded && (
          <CardContent className="space-y-4 pt-0">
            <p className="text-sm text-muted-foreground">
              Link internal agents to their Property24 external agent IDs. Once linked, imported properties
              from that external agent will auto-map to the correct internal agent during sync.
            </p>

            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search agents..."
                  value={agentMapSearch}
                  onChange={(e) => setAgentMapSearch(e.target.value)}
                  className="pl-8 h-8 text-xs"
                />
              </div>
              <Button
                data-testid="admin-property-refresh-agents-button"
                variant="outline"
                size="sm"
                onClick={loadAgentMapData}
                disabled={isLoadingAgentMap}
              >
                {isLoadingAgentMap ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
              </Button>
            </div>

            {isLoadingAgentMap ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
                <span className="text-sm text-muted-foreground">Loading agents...</span>
              </div>
            ) : filteredAgentMap.length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {agentMapSearch ? 'No agents match your search' : 'No agents found. Agents will appear here once synced.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredAgentMap.map((agent) => {
                  const isEditing = editingAgentLink === agent.id;
                  const isLinked = !!agent.externalAgentId;

                  return (
                    <div
                      key={agent.id}
                      className={`flex items-center gap-3 rounded-lg p-3 transition-colors ${
                        isLinked
                          ? 'bg-green-500/5 border border-green-500/20'
                          : 'bg-muted/30 border border-muted'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                        isLinked ? 'bg-green-500/20 text-green-400' : 'bg-muted text-muted-foreground'
                      }`}>
                        {agent.name?.charAt(0) || '?'}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">{agent.name}</p>
                          {isLinked && (
                            <UserCheck className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{agent.email}</p>
                        <p className="text-[10px] text-muted-foreground font-mono truncate">ID: {agent.id}</p>
                      </div>

                      <div className="shrink-0 flex items-center gap-2">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <Input
                              value={editingExternalId}
                              onChange={(e) => setEditingExternalId(e.target.value)}
                              placeholder="External Agent ID"
                              className="h-7 text-xs w-40"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  (async () => {
                                    setIsSavingAgentLink(true);
                                    try {
                                      await propertyAPI.linkAgentExternal(agent.id, editingExternalId);
                                      toast.success(`Agent "${agent.name}" linked to external ID: ${editingExternalId || '(cleared)'}`);
                                      setEditingAgentLink(null);
                                      loadAgentMapData();
                                    } catch (err: any) {
                                      toast.error(err?.message || 'Failed to link agent');
                                    } finally {
                                      setIsSavingAgentLink(false);
                                    }
                                  })();
                                } else if (e.key === 'Escape') {
                                  setEditingAgentLink(null);
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              disabled={isSavingAgentLink}
                              onClick={async () => {
                                setIsSavingAgentLink(true);
                                try {
                                  await propertyAPI.linkAgentExternal(agent.id, editingExternalId);
                                  toast.success(`Agent "${agent.name}" linked to external ID: ${editingExternalId || '(cleared)'}`);
                                  setEditingAgentLink(null);
                                  loadAgentMapData();
                                } catch (err: any) {
                                  toast.error(err?.message || 'Failed to link agent');
                                } finally {
                                  setIsSavingAgentLink(false);
                                }
                              }}
                            >
                              {isSavingAgentLink ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => setEditingAgentLink(null)}
                              disabled={isSavingAgentLink}
                            >
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            {isLinked ? (
                              <Badge className="bg-green-500/10 text-green-400 border border-green-500/30 text-[10px] font-mono">
                                ext:{agent.externalAgentId}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] text-muted-foreground">
                                Not linked
                              </Badge>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                setEditingAgentLink(agent.id);
                                setEditingExternalId(agent.externalAgentId || '');
                              }}
                              title={isLinked ? 'Edit external link' : 'Link to external agent'}
                            >
                              {isLinked ? (
                                <Edit className="w-3.5 h-3.5" />
                              ) : (
                                <Link2 className="w-3.5 h-3.5" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {agentMapList.length > 0 && (
              <div className="grid grid-cols-3 gap-3 pt-2">
                <div className="bg-muted/50 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold">{agentMapList.length}</p>
                  <p className="text-[10px] text-muted-foreground">Total Agents</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-green-500">{agentMapList.filter(a => a.externalAgentId).length}</p>
                  <p className="text-[10px] text-muted-foreground">Linked</p>
                </div>
                <div className="bg-amber-500/10 rounded-lg p-2.5 text-center">
                  <p className="text-lg font-bold text-amber-500">{agentMapList.filter(a => !a.externalAgentId).length}</p>
                  <p className="text-[10px] text-muted-foreground">Unlinked</p>
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ═══ Danger Zone — Purge All ═══ */}
      <Card className="border-red-500/30 bg-red-500/5">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              <CardTitle className="text-base text-red-400">Danger Zone</CardTitle>
            </div>
            {!showPurgeConfirm ? (
              <Button
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => setShowPurgeConfirm(true)}
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Purge All Listings
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-400 font-medium">
                  Delete all {properties.length} properties + views? This cannot be undone.
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPurgeConfirm(false)}
                  disabled={isPurging}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handlePurgeAll}
                  disabled={isPurging}
                >
                  {isPurging ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      Purging...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Yes, Purge Everything
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* ═══ Listing Status Management ═══ */}
      <Card className="border-purple-500/30 bg-purple-500/5">
        <CardHeader
          className="cursor-pointer select-none py-3 px-4"
          onClick={() => setStatusPanelExpanded(!statusPanelExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-500" />
              <CardTitle className="text-base">Listing Status Management</CardTitle>
              {externalProperties.length > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {externalProperties.length} external listing{externalProperties.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            {statusPanelExpanded ? (
              <ChevronUp className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        {statusPanelExpanded && (
          <CardContent className="space-y-4 pt-0">
            <p className="text-sm text-muted-foreground">
              Update listing status for properties synced from external portals. Uses the v2{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">PUT /listings/{'{somethingPropertyId}'}</code>{' '}
              endpoint with fallback to legacy POST.
            </p>

            {/* SP ID Backfill Section */}
            {propertiesMissingSpId > 0 && (
              <Alert className="border-amber-500/30 bg-amber-500/5">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <AlertTitle className="text-amber-400 text-sm">SP ID Backfill Available</AlertTitle>
                <AlertDescription className="text-amber-400/80 text-xs">
                  {propertiesMissingSpId} propert{propertiesMissingSpId !== 1 ? 'ies' : 'y'} synced before v2 lack a{' '}
                  <code className="bg-muted px-1 py-0.5 rounded">somethingPropertyId</code>. Backfill constructs IDs using{' '}
                  <code className="bg-muted px-1 py-0.5 rounded">SP-{'{SOURCE}'}-{'{EXTERNAL_ID}'}</code>.
                </AlertDescription>
                <div className="mt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBackfillSpIds}
                    disabled={isBackfilling}
                    className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  >
                    {isBackfilling ? (
                      <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                    ) : (
                      <Zap className="w-3.5 h-3.5 mr-2" />
                    )}
                    {isBackfilling ? 'Backfilling...' : `Backfill ${propertiesMissingSpId} SP ID${propertiesMissingSpId !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              </Alert>
            )}

            {/* Backfill results */}
            {backfillResults && (
              <Alert className="border-green-500/30 bg-green-500/5">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <AlertTitle className="text-green-400 text-sm">Backfill Complete</AlertTitle>
                <AlertDescription className="text-green-400/80 text-xs">
                  {backfillResults.backfilled} backfilled, {backfillResults.skipped} skipped, {backfillResults.errors} errors out of {backfillResults.total} total.
                </AlertDescription>
                {backfillResults.details.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto space-y-0.5">
                    {backfillResults.details.map((d) => (
                      <div key={d.id} className="text-[10px] font-mono text-green-400/60">
                        {d.id} → {d.somethingPropertyId}
                      </div>
                    ))}
                  </div>
                )}
              </Alert>
            )}

            {/* Search & property list */}
            <div className="relative max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                placeholder="Search external properties..."
                value={statusSearchTerm}
                onChange={(e) => setStatusSearchTerm(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>

            {externalProperties.length === 0 ? (
              <div className="text-center py-6">
                <ExternalLink className="w-10 h-10 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No externally-linked properties found. Sync from an external portal first.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {filteredStatusProperties.map((property) => {
                  const spId = getSomethingPropertyId(property);
                  const currentStatus = property.listingStatus || 'Active';
                  const statusClass = statusColorMap[currentStatus] || statusColorMap.Active;

                  return (
                    <div
                      key={property.id}
                      className="flex items-center gap-3 rounded-lg p-3 bg-muted/30 border border-muted hover:border-purple-500/30 transition-colors"
                    >
                      <img
                        src={property.media?.[0]?.url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100'}
                        alt={property.title}
                        className="w-10 h-10 rounded-lg object-cover shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{property.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {spId && (
                            <code className="text-[10px] bg-muted px-1 py-0.5 rounded font-mono text-muted-foreground truncate max-w-[180px]">
                              {spId}
                            </code>
                          )}
                          {!spId && property.externalId && (
                            <code className="text-[10px] bg-muted px-1 py-0.5 rounded font-mono text-muted-foreground">
                              ext:{property.externalId}
                            </code>
                          )}
                        </div>
                      </div>
                      <Badge className={`text-[10px] border shrink-0 ${statusClass}`}>
                        {currentStatus}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        className="shrink-0 h-7 text-xs border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                        onClick={() => openStatusDialog(property)}
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        Update
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Status stats */}
            {externalProperties.length > 0 && (
              <div className="grid grid-cols-4 gap-2 pt-2">
                {(['Active', 'Pending', 'Sold', 'Withdrawn'] as const).map(status => {
                  const count = externalProperties.filter(p => (p.listingStatus || 'Active') === status).length;
                  const colors = statusColorMap[status];
                  return (
                    <div key={status} className={`rounded-lg p-2 text-center border ${colors}`}>
                      <p className="text-lg font-bold">{count}</p>
                      <p className="text-[10px]">{status}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Filters and Search */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Badge
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => onStatusFilterChange('all')}
          >
            All ({stats.total})
          </Badge>
          <Badge
            variant={statusFilter === 'Published' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => onStatusFilterChange('Published')}
          >
            Published ({stats.published})
          </Badge>
          <Badge
            variant={statusFilter === 'Draft' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => onStatusFilterChange('Draft')}
          >
            Draft ({stats.draft})
          </Badge>
          <Badge
            variant={statusFilter === 'In-Review' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => onStatusFilterChange('In-Review')}
          >
            In Review ({stats.inReview})
          </Badge>
          <Badge
            variant={statusFilter === 'Archived' ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => onStatusFilterChange('Archived')}
          >
            Archived ({stats.archived})
          </Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Source filter */}
          <div className="flex items-center gap-1 border rounded-md px-2 py-1 bg-muted/30">
            <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
            {(['all', 'property24', 'seller', 'manual'] as const).map(src => (
              <Badge
                key={src}
                variant={sourceFilter === src ? 'default' : 'outline'}
                className="cursor-pointer text-xs"
                onClick={() => setSourceFilter(src)}
              >
                {src === 'all' ? 'All Sources' : src === 'property24' ? 'P24' : src.charAt(0).toUpperCase() + src.slice(1)}
              </Badge>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search listings..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          {selectedProperties.length > 0 && (
            <Button data-testid="admin-property-bulk-archive-button" variant="destructive" size="sm" onClick={onBulkArchive}>
              <Archive className="w-4 h-4 mr-2" />
              Archive {selectedProperties.length}
            </Button>
          )}
        </div>
      </div>

      {/* Properties Table */}
      <Card>
        <div className="p-3 border-b bg-muted/30">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" />
            <span>Click on any property row to view full details and manage agent assignments</span>
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedProperties.length === properties.length && properties.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      properties.forEach(p => onToggleSelection(p.id));
                    } else {
                      properties.forEach(p => onToggleSelection(p.id));
                    }
                  }}
                />
              </TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Agent</TableHead>
              <TableHead className="w-8"><Star className="w-3.5 h-3.5" /></TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-primary" />
                  <p className="text-muted-foreground">Loading listings...</p>
                </TableCell>
              </TableRow>
            ) : properties.filter(p => sourceFilter === 'all' || (p as any).source === sourceFilter || (!((p as any).source) && sourceFilter === 'manual')).length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <Home className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground mb-4">No listings found</p>
                  <Button onClick={onCreateProperty}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Listing
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              properties
                .filter(p => sourceFilter === 'all' || (p as any).source === sourceFilter || (!((p as any).source) && sourceFilter === 'manual'))
                .map((property) => (
                <TableRow
                  key={property.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={(e) => {
                    // Don't trigger row click if clicking on interactive elements
                    const target = e.target as HTMLElement;
                    if (
                      target.closest('button') || 
                      target.closest('[role="checkbox"]') ||
                      target.closest('input[type="checkbox"]')
                    ) {
                      return;
                    }
                    handlePropertyClick(property);
                  }}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedProperties.includes(property.id)}
                      onCheckedChange={() => onToggleSelection(property.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <img
                        src={property.media?.[0]?.url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400'}
                        alt={property.title}
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400';
                        }}
                      />
                      <div>
                        <p className="font-medium">{property.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {property.facts?.bedrooms || 0}bed • {property.facts?.bathrooms || 0}bath
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge
                        variant={
                          property.status === 'Published' ? 'default' :
                          property.status === 'In-Review' ? 'secondary' :
                          property.status === 'Archived' ? 'destructive' : 'outline'
                        }
                      >
                        {property.status}
                      </Badge>
                      {property.listingStatus && property.listingStatus !== 'Active' && (
                        <Badge className={`text-[9px] border ${statusColorMap[property.listingStatus] || ''}`}>
                          {property.listingStatus}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  {/* Source badge */}
                  <TableCell>
                    {(() => {
                      const src = (property as any).source || 'manual';
                      if (src === 'property24') return <Badge className="text-[9px] bg-blue-500/20 text-blue-400 border border-blue-500/30">P24</Badge>;
                      if (src === 'seller') return <Badge className="text-[9px] bg-orange-500/20 text-orange-400 border border-orange-500/30">Seller</Badge>;
                      return <Badge className="text-[9px] bg-muted text-muted-foreground border">Manual</Badge>;
                    })()}
                  </TableCell>
                  <TableCell>R{(property.price || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    {property.address?.suburb || 'N/A'}
                    {property.address?.city ? `, ${property.address.city}` : ''}
                  </TableCell>
                  {/* Agent cell — show photo + name */}
                  <TableCell>
                    {(() => {
                      const agentName = property.agent?.name || (property as any).agentName;
                      const agentPhoto = (property as any).agentPhotoUrl;
                      if (!agentName) return <span className="text-xs text-muted-foreground italic">Unassigned</span>;
                      return (
                        <div className="flex items-center gap-1.5">
                          {agentPhoto ? (
                            <img src={agentPhoto} alt={agentName} className="w-6 h-6 rounded-full object-cover" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold">
                              {agentName.charAt(0)}
                            </div>
                          )}
                          <span className="text-xs truncate max-w-[80px]">{agentName}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  {/* Featured star cell */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`p-1 h-7 w-7 ${(property as any).featured ? 'text-yellow-400' : 'text-muted-foreground'}`}
                      onClick={() => handleFeatureToggle(property)}
                      disabled={featuredTogglingId === ((property as any).somethingPropertyId?.replace('SP-PROPERTY24-', '') || property.id)}
                      title={(property as any).featured ? 'Remove from featured' : 'Mark as featured'}
                    >
                      {featuredTogglingId === ((property as any).somethingPropertyId?.replace('SP-PROPERTY24-', '') || property.id) ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Star className={`w-3.5 h-3.5 ${(property as any).featured ? 'fill-yellow-400' : ''}`} />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePropertyClick(property)}
                        title="View details"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {/* Edit only for manual/seller listings; for P24 just assign agent */}
                      {((property as any).source === 'manual' || (property as any).source === 'seller' || !(property as any).source) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditProperty(property)}
                          title="Edit listing"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      {(property.somethingPropertyId || property.externalId) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStatusDialog(property)}
                          title="Update listing status"
                        >
                          <Tag className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openLinkDialog(property)}
                        title="Assign agent"
                        className="text-primary"
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                      {property.status === 'Archived' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRestoreProperty(property)}
                          title="Restore property"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onArchiveProperty(property)}
                          title="Archive property"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Dialog - Same as AgentHub */}
      <Dialog open={isCreatingProperty} onOpenChange={onCloseDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProperty ? 'Edit Property' : 'Create New Property'}</DialogTitle>
            <DialogDescription>
              {editingProperty ? 'Update property details' : 'Fill in property details to create a new listing'}
            </DialogDescription>
          </DialogHeader>
          
          <form className="space-y-6" onSubmit={onSubmit}>
            {/* Image Upload Section */}
            <div className="space-y-3">
              <Label>Property Images *</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onImageUpload}
                  className="hidden"
                  id="admin-image-upload"
                  disabled={isUploadingImage}
                />
                <label
                  htmlFor="admin-image-upload"
                  className="flex flex-col items-center justify-center cursor-pointer py-4"
                >
                  {isUploadingImage ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-2" />
                      {uploadProgress && (
                        <p className="text-sm text-muted-foreground">
                          Uploading {uploadProgress.current} of {uploadProgress.total}...
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload multiple images (max 10MB each)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        First image will be the cover photo
                      </p>
                    </>
                  )}
                </label>
                
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.url}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-transparent group-hover:border-primary/50 transition-all"
                        />
                        
                        {/* Image controls */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                          {/* Move left */}
                          {index > 0 && onMoveImage && (
                            <button
                              type="button"
                              onClick={() => onMoveImage(index, 'left')}
                              className="bg-white/90 hover:bg-white text-black rounded-full p-1.5"
                              title="Move left"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                          )}
                          
                          {/* Remove */}
                          <button
                            type="button"
                            onClick={() => onRemoveImage(index)}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                            title="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          
                          {/* Move right */}
                          {index < uploadedImages.length - 1 && onMoveImage && (
                            <button
                              type="button"
                              onClick={() => onMoveImage(index, 'right')}
                              className="bg-white/90 hover:bg-white text-black rounded-full p-1.5"
                              title="Move right"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        
                        {/* Cover badge */}
                        {index === 0 && (
                          <Badge className="absolute top-2 left-2 text-xs bg-primary text-primary-foreground">
                            Cover Photo
                          </Badge>
                        )}
                        
                        {/* Image number */}
                        <Badge variant="secondary" className="absolute bottom-2 right-2 text-xs">
                          {index + 1}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Image count */}
                {uploadedImages.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {uploadedImages.length} image{uploadedImages.length !== 1 ? 's' : ''} uploaded
                  </p>
                )}
              </div>
            </div>

            {/* Form Fields - Simplified for brevity */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin-title">Property Title *</Label>
                <Input
                  id="admin-title"
                  value={formData.title}
                  onChange={(e) => onFormChange({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-type">Property Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => onFormChange({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="House">House</SelectItem>
                    <SelectItem value="Apartment">Apartment</SelectItem>
                    <SelectItem value="Townhouse">Townhouse</SelectItem>
                    <SelectItem value="Farm">Farm</SelectItem>
                    <SelectItem value="VacantLand">Vacant Land</SelectItem>
                    <SelectItem value="Land">Land</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin-price">Price (ZAR) *</Label>
                <Input
                  id="admin-price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => onFormChange({ ...formData, price: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-suburb">Suburb *</Label>
                <Input
                  id="admin-suburb"
                  value={formData.suburb}
                  onChange={(e) => onFormChange({ ...formData, suburb: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-city">City *</Label>
              <Input
                id="admin-city"
                value={formData.city}
                onChange={(e) => onFormChange({ ...formData, city: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-description">Description</Label>
              <Textarea
                id="admin-description"
                value={formData.description}
                onChange={(e) => onFormChange({ ...formData, description: e.target.value })}
                rows={5}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.publishImmediately}
                onCheckedChange={(checked) => onFormChange({ ...formData, publishImmediately: checked })}
              />
              <Label>Publish immediately</Label>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button data-testid="admin-property-form-cancel-button" type="button" variant="outline" onClick={onCloseDialog} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button data-testid="admin-property-form-submit-button" type="submit" disabled={isSubmitting || isUploadingImage}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingProperty ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingProperty ? 'Update Property' : 'Create Property'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Property Detail Modal */}
      <PropertyDetailModal
        property={selectedProperty}
        open={showDetailModal}
        onClose={handleCloseDetailModal}
        onUpdated={handlePropertyUpdated}
      />

      {/* ═══ Assign Agent Dialog ═══ */}
      <Dialog open={showLinkDialog} onOpenChange={(open) => { if (!open) { setShowLinkDialog(false); setLinkingProperty(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Assign Agent
            </DialogTitle>
            <DialogDescription>
              {linkingProperty ? (
                <>Assign an agent to <strong>{linkingProperty.title}</strong>.</>
              ) : (
                'Assign an agent to this listing.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current agent summary */}
            {linkingProperty && (
              <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center gap-3">
                  <img
                    src={linkingProperty.media?.[0]?.url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100'}
                    alt={linkingProperty.title}
                    className="w-10 h-10 rounded-lg object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100'; }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{linkingProperty.title}</p>
                    <p className="text-xs text-muted-foreground">
                      Current agent: <span className="font-medium">{(linkingProperty as any).agentName || linkingProperty.agentId || 'None'}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Separator />

            {/* Agent selector */}
            {isLoadingDropdowns ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
                <span className="text-sm text-muted-foreground">Loading agents...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Agent selector */}
                <div className="space-y-1.5">
                  <Label className="text-sm">
                    Assign Agent <span className="text-muted-foreground text-xs">({agentsList.length} available)</span>
                  </Label>
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search agents by name, email, or ID..."
                        value={agentSearchTerm}
                        onChange={(e) => setAgentSearchTerm(e.target.value)}
                        className="pl-8 h-8 text-xs"
                      />
                    </div>
                    <div className="border rounded-lg max-h-36 overflow-y-auto">
                      <div
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors text-xs border-b ${
                          !linkForm.agentId ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'
                        }`}
                        onClick={() => setLinkForm(prev => ({ ...prev, agentId: '' }))}
                      >
                        <Unlink className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="italic text-muted-foreground">No agent (unlink)</span>
                      </div>
                      {filteredAgentsDropdown.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-muted-foreground text-center">
                          {agentSearchTerm ? 'No agents match your search' : 'No agents found'}
                        </div>
                      ) : (
                        filteredAgentsDropdown.map((agent) => (
                          <div
                            key={agent.id}
                            className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors text-xs ${
                              linkForm.agentId === agent.id
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'hover:bg-muted/50'
                            }`}
                            onClick={() => setLinkForm(prev => ({ ...prev, agentId: agent.id }))}
                          >
                            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-bold shrink-0">
                              {agent.name?.charAt(0) || '?'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{agent.name}</p>
                              <p className="text-muted-foreground truncate text-[10px]">{agent.email}</p>
                            </div>
                            {agent.externalAgentId && (
                              <Badge variant="outline" className="text-[9px] shrink-0 px-1 py-0">
                                ext:{agent.externalAgentId}
                              </Badge>
                            )}
                            {linkForm.agentId === agent.id && (
                              <CheckCircle className="w-3.5 h-3.5 text-primary shrink-0" />
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                data-testid="admin-property-link-cancel-button"
                variant="outline"
                onClick={() => { setShowLinkDialog(false); setLinkingProperty(null); }}
                disabled={isLinking}
              >
                Cancel
              </Button>
              <Button data-testid="admin-property-link-save-button" onClick={handleLinkProperty} disabled={isLinking}>
                {isLinking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Assign Agent
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══ Listing Status Update Dialog ═══ */}
      <Dialog open={showStatusDialog} onOpenChange={(open) => { if (!open) { setShowStatusDialog(false); setStatusUpdateProperty(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-500" />
              Update Listing Status
            </DialogTitle>
            <DialogDescription>
              {statusUpdateProperty ? (
                <>Change listing status for <strong>{statusUpdateProperty.title}</strong> via the external API.</>
              ) : (
                'Update the listing status on the external portal.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Property info */}
            {statusUpdateProperty && (
              <div className="bg-muted/50 rounded-lg p-3 space-y-1.5">
                <div className="flex items-center gap-3">
                  <img
                    src={statusUpdateProperty.media?.[0]?.url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100'}
                    alt={statusUpdateProperty.title}
                    className="w-12 h-12 rounded-lg object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=100';
                    }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{statusUpdateProperty.title}</p>
                    <p className="text-xs text-muted-foreground">R{statusUpdateProperty.price?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div>
                    <span className="text-muted-foreground">SP ID:</span>{' '}
                    <code className="bg-muted px-1 py-0.5 rounded font-mono text-[10px]">
                      {getSomethingPropertyId(statusUpdateProperty) || 'N/A'}
                    </code>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Current:</span>{' '}
                    <Badge className={`text-[10px] border ${statusColorMap[statusUpdateProperty.listingStatus || 'Active'] || statusColorMap.Active}`}>
                      {statusUpdateProperty.listingStatus || 'Active'}
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            <Separator />

            {/* Status selection */}
            <div className="space-y-1.5">
              <Label className="text-sm">New Status *</Label>
              <Select value={statusForm.status} onValueChange={(value) => setStatusForm(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  {LISTING_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          status === 'Active' ? 'bg-emerald-500' :
                          status === 'Pending' ? 'bg-amber-500' :
                          status === 'Sold' ? 'bg-red-500' :
                          status === 'Rented' ? 'bg-purple-500' :
                          status === 'Expired' ? 'bg-slate-500' :
                          status === 'Withdrawn' ? 'bg-gray-500' :
                          'bg-rose-500'
                        }`} />
                        {status}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional fields */}
            <div className="space-y-1.5">
              <Label htmlFor="status-listing-number" className="text-sm">
                Listing Number <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Input
                id="status-listing-number"
                placeholder="e.g. P24-12345"
                value={statusForm.listingNumber}
                onChange={(e) => setStatusForm(prev => ({ ...prev, listingNumber: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="status-listing-url" className="text-sm">
                Listing URL <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Input
                id="status-listing-url"
                placeholder="https://..."
                value={statusForm.listingUrl}
                onChange={(e) => setStatusForm(prev => ({ ...prev, listingUrl: e.target.value }))}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="status-message" className="text-sm">
                Message <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
              <Textarea
                id="status-message"
                placeholder="Reason for status change..."
                value={statusForm.message}
                onChange={(e) => setStatusForm(prev => ({ ...prev, message: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button
                data-testid="admin-property-status-cancel-button"
                variant="outline"
                onClick={() => { setShowStatusDialog(false); setStatusUpdateProperty(null); }}
                disabled={isUpdatingStatus}
              >
                Cancel
              </Button>
              <Button
                data-testid="admin-property-status-update-button"
                onClick={handleUpdateListingStatus}
                disabled={isUpdatingStatus || !statusForm.status}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isUpdatingStatus ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Update Status
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}